<template>
    <div>
        <slot />
    </div>
</template>

<script>
/**
 * This component should contain one or more <a> elements.
 * When the user clicks on one of them, the link will open in a new tab and the current tab will be redirected to the given href prop.
 * This functionality can be enabled or disabled as needed.
 */
export default {
    name: "APopunder",
    props: {
        enabled: {type: Boolean, default: true},
        href: {type: String},
        activationTimeMs: {type: Number, default: 750},
    },

    data() {
        return {
            timer: null,
            wasViable: false,
            previousLinks: [],
        };
    },

    computed: {
        isViable() {
            return Boolean(this.enabled && this.href && this.href.length);
        }
    },

    mounted() {
        this.wasViable = this.isViable;
        if (this.isViable) {
            this.install();
        }
    },

    beforeUpdate() {
        if (this.isViable && this.wasViable) {
            this.uninstall();
        }
    },

    updated() {
        if (this.isViable && this.wasViable) {
            this.install();
        }
    },

    beforeDestroy() {
        if (this.isViable) {
            this.uninstall();
        }
    },

    watch: {
        isViable(newValue, oldValue) {
            this.wasViable = oldValue;

            if (newValue && !oldValue) {
                this.install();
            } else if (oldValue && !newValue) {
                this.uninstall();
            }

            this.$nextTick(() => {
                this.wasViable = this.isViable;
            });
        }
    },

    methods: {
        install() {
            this.$nextTick(() =>
                this.isViable && this.getLinks().forEach(a => {
                    const target = a.getAttribute('target');
                    this.previousLinks.push({a, target});
                    a.setAttribute('target', '_blank');
                    a.addEventListener('click', this.pop, {passive: true});
                })
            );
        },

        uninstall() {
            this.getLinks().forEach(a => {
                const entry = this.previousLinks.find(entry => entry.a === a);
                if (entry && entry.target) {
                    a.setAttribute('target', entry.target);
                } else {
                    a.removeAttribute('target');
                }
                a.removeEventListener('click', this.pop);
            });
            this.previousLinks = [];
        },

        getLinks() {
            return Array.from(this.$el.getElementsByTagName('a'));
        },

        pop(e) {
            if (e.defaultPrevented) {
                return;
            }

            if (!this.isViable) {
                return;
            }

            // The first time the pop is called
            if (!this.timer) {
                // This event happens instantly, before navigation
                this.$emit('beforepop');
            }

            // No activation time - navigate instantly
            if (!this.activationTimeMs) {
                this.navigate();
                return;
            }

            // Reset the timer if it's been set
            if (this.timer) {
                clearTimeout(this.timer);
                this.timer = null;
            }

            // Schedule the navigation
            this.timer = setTimeout(() => this.navigate(), this.activationTimeMs);
        },

        navigate() {
            window.location.href = this.href;
        }
    }
}
</script>
